import { useUpdateBillingPreferences } from '@features/applications/api/update-billing-preferences.ts';
import {
  WithApplicationFromRoute,
  WithApplicationFromRouteProps
} from '@features/applications/components/with-application-from-route.tsx';
import { WorkableApplicationStepGuard } from '@features/applications/components/workable-application-step-guard.tsx';
import { mapBillingPreferencesFormToRequest } from '@features/billing-preferences/billing-preferences.mappers.ts';
import { BillingPreferencesForm } from '@features/billing-preferences/billing-preferences-form.ts';
import { BillingOptionsComparison } from '@features/billing-preferences/components/billing-options-comparision.tsx';
import {
  BillingPreferencesSelection
} from '@features/billing-preferences/components/billing-preferences-selection.tsx';
import { FeeBreakdown } from '@features/billing-preferences/components/fee-breakdown.tsx';
import { ServiceAvailabilityAlert } from '@features/wizard/components/service-availability-alert.tsx';
import useWizardRouter from '@features/wizard/wizard-router/use-wizard-router.ts';
import { WIZARD_STEP } from '@features/wizard/wizard-router/wizard-router.constants.ts';
import { Alert, Box, Typography } from '@mui/material';
import Grid from '@mui/system/Unstable_Grid';
import { FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { Navigate } from 'react-router-dom';

import AddressView from '@/components/address-view.tsx';
import useFormSubmitHandler from '@/core/forms/use-form-submit-handler.ts';
import SubmitButton from '@/core/ui/components/submit-button.tsx';

const BillingPreferencesStepContainer = ({ application }: BillingPreferencesStepContainerProps) => {
  const { getStepRoute } = useWizardRouter({ applicationId: application.id });
  const form = useForm<BillingPreferencesForm>({
    defaultValues: {
      services: application.serviceLocation.services.map(service => ({
        id: service.id,
        billingPreference: service.billingPreference ?? (service.isPreferenceEditable ? 'prepaid' : 'standard')
      }))
    }
  });
  const {
    handleSubmit,
    control
  } = form;
  const { handleError } = useFormSubmitHandler({ form });

  const {
    mutate: updateBillingPreferences,
    isPending: updateBillingPreferencesIsPending,
    isSuccess: updateBillingPreferencesSuccess
  } = useUpdateBillingPreferences();

  const {
    fees,
    serviceLocation: { services, serviceAddress }
  } = application;

  const serviceBillingPreferences = useWatch({ control, name: 'services' });

  const availableServices = services?.filter(service => service.status === 'residential');

  const onSubmit: SubmitHandler<BillingPreferencesForm> = (data) => {
    const request = mapBillingPreferencesFormToRequest(data);
    updateBillingPreferences({
      applicationId: application.id,
      data: request
    }, {
      onError: handleError
    });
  };

  if (updateBillingPreferencesSuccess) {
    return <Navigate to={getStepRoute(WIZARD_STEP.CONFIRM)} />;
  }

  return (
    <WorkableApplicationStepGuard application={application}>
      <FormProvider {...form}>
        <Grid
          container
          spacing={4}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Grid xs={12}>
            <Typography variant="h1" gutterBottom>Billing Preferences</Typography>
            {serviceAddress ?
                (
                  <div>
                    <Typography marginTop={4} variant="h2" gutterBottom>Service Location</Typography>
                    <Typography gutterBottom>
                      <AddressView address={serviceAddress} />
                    </Typography>
                  </div>
                ) :
                <></> }
            <Box marginTop={4} gap={4} marginBottom={4}>
              <ServiceAvailabilityAlert services={services} />
              <Alert severity="info">
                <span>
                  A credit review has been completed and we’ve checked system information. Please review Fee information, make selection (if applicable), and select Next to proceed.
                </span>
              </Alert>
            </Box>
            <Box marginBottom={4}>
              <BillingOptionsComparison />
            </Box>
            <Box>
              <Grid container spacing={3}>
                <Grid xs={12} sm={6}>
                  <BillingPreferencesSelection availableServices={availableServices ?? []} />
                </Grid>
                <Grid xs={12} sm={6}>
                  <FeeBreakdown services={services} fees={fees} serviceBillingPreferences={serviceBillingPreferences} />
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid xs={12} display="flex" justifyContent="end">
            <SubmitButton isSubmitting={updateBillingPreferencesIsPending}>Next</SubmitButton>
          </Grid>
        </Grid>
      </FormProvider>
    </WorkableApplicationStepGuard>
  );
};

type BillingPreferencesStepContainerProps = object & WithApplicationFromRouteProps;

const BillingPreferencesPage = WithApplicationFromRoute(BillingPreferencesStepContainer);

export default BillingPreferencesPage;
